import { authenticatedFetch } from '../config/api';

export enum ExchangeErrorCode {
    UnsupportedCurrency = 'UNSUPPORTED_CURRENCY',
    NetworkError = 'NETWORK_ERROR',
    ParseError = 'PARSE_ERROR',
    NotFound = 'RATE_NOT_FOUND',
}

export class EcbExchangeError extends Error {
    code: ExchangeErrorCode;
    constructor(code: ExchangeErrorCode, message: string) {
        super(message);
        this.code = code;
        this.name = 'EcbExchangeError';
    }
}

export type ExchangeError = EcbExchangeError;

export type ExchangeRatesMap = Record<string, number>; // targetCode -> rate (target per base)

// ECB supported currency codes
export const SUPPORTED_ECB_CODES: ReadonlySet<string> = new Set([
    'EUR',
    'ARS', 'AUD', 'BGN', 'BRL', 'CAD', 'CHF', 'CNY', 'CYP', 'CZK', 'DKK', 'DZD', 'EEK', 'GBP', 'GRD', 'HKD', 'HRK', 'HUF', 'IDR', 'ILS', 'INR', 'ISK', 'JPY', 'KRW', 'LTL', 'LVL', 'MAD', 'MTL', 'MXN', 'MYR', 'NOK', 'NZD', 'PHP', 'PLN', 'RON', 'RUB', 'SEK', 'SGD', 'SIT', 'SKK', 'THB', 'TRY', 'TWD', 'USD', 'ZAR'
]);

// Public function: given a base currency code, return conversion rates into others from backend cache
export async function getExchangeRatesFrom(baseCode: string): Promise<{ base: string; period?: string; rates: ExchangeRatesMap }> {
    const code = (baseCode || '').toUpperCase();
    
    try {
        const response = await authenticatedFetch(`/api/exchange-rates?base=${code}`, {
            // Use frontend caching to show old results while new ones load
            cacheKey: `exchange-rates-${code}`,
            ttl: 1000 * 60 * 60, // 1 hour frontend cache
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        return {
            base: data.base,
            rates: data.rates,
            period: data.period
        };
    } catch (e: any) {
        console.error('Error fetching exchange rates from backend:', e);
        throw new EcbExchangeError(ExchangeErrorCode.NetworkError, e.message || 'Failed to fetch rates');
    }
}

// Convenience: list supported codes
export function getSupportedEcbCodes(): string[] { return Array.from(SUPPORTED_ECB_CODES).sort(); }

// Lightweight self-test (non-throwing) to ensure basic invariants
export async function selfTestEcbRates(): Promise<{ ok: boolean; message?: string }> {
    try {
        const res = await getExchangeRatesFrom('USD');
        if (!res.rates['EUR']) return { ok: false, message: 'Missing EUR rate for USD base' };
        return { ok: true };
    } catch (e: any) {
        return { ok: false, message: e?.message || String(e) };
    }
}


